// Registry of every OpenAI model the app may call. Prices are USD per 1M tokens
// (standard tier, ≤272K context) from https://developers.openai.com/api/docs/pricing.

export type AiReasoningEffort = 'none' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';
export type AiVerbosity = 'low' | 'medium' | 'high';
export type AiModelFamily = 'gpt-5.4' | 'gpt-5.6' | 'gpt-6';

export interface AiModelPricing {
  inputPerMTok: number;
  cachedInputPerMTok: number;
  /** Billed only when the API reports cache-write tokens; null when the model has no cache-write rate. */
  cacheWritePerMTok: number | null;
  outputPerMTok: number;
}

export interface AiModel {
  id: string;
  label: string;
  family: AiModelFamily;
  /**
   * `explicit`: GPT-5.6+ — cache breakpoints placed after the static prompt; writes billed at the cache-write rate.
   * `key`: earlier models — implicit caching, routed by a stable `prompt_cache_key`.
   */
  promptCaching: 'explicit' | 'key';
  description: string;
  reasoningEfforts: readonly AiReasoningEffort[];
  pricing: AiModelPricing;
}

const GPT_5_4_EFFORTS = ['none', 'low', 'medium', 'high', 'xhigh'] as const satisfies readonly AiReasoningEffort[];
const GPT_5_6_EFFORTS = ['none', 'low', 'medium', 'high', 'xhigh', 'max'] as const satisfies readonly AiReasoningEffort[];

export const AI_MODELS = {
  'gpt-5.4-nano': {
    id: 'gpt-5.4-nano',
    label: 'GPT-5.4 nano',
    family: 'gpt-5.4',
    promptCaching: 'key',
    description: 'Previous generation, smallest.',
    reasoningEfforts: GPT_5_4_EFFORTS,
    pricing: { inputPerMTok: 0.2, cachedInputPerMTok: 0.02, cacheWritePerMTok: null, outputPerMTok: 1.25 },
  },
  'gpt-5.4-mini': {
    id: 'gpt-5.4-mini',
    label: 'GPT-5.4 mini',
    family: 'gpt-5.4',
    promptCaching: 'key',
    description: 'Previous generation, balanced.',
    reasoningEfforts: GPT_5_4_EFFORTS,
    pricing: { inputPerMTok: 0.75, cachedInputPerMTok: 0.075, cacheWritePerMTok: null, outputPerMTok: 4.5 },
  },
  'gpt-5.4': {
    id: 'gpt-5.4',
    label: 'GPT-5.4',
    family: 'gpt-5.4',
    promptCaching: 'key',
    description: 'Previous generation, full size.',
    reasoningEfforts: GPT_5_4_EFFORTS,
    pricing: { inputPerMTok: 2.5, cachedInputPerMTok: 0.25, cacheWritePerMTok: null, outputPerMTok: 15 },
  },
  'gpt-5.6-luna': {
    id: 'gpt-5.6-luna',
    label: 'GPT-5.6 Luna',
    family: 'gpt-5.6',
    promptCaching: 'explicit',
    description: 'Fast and cheap; good for well-constrained extraction.',
    reasoningEfforts: GPT_5_6_EFFORTS,
    pricing: { inputPerMTok: 0.2, cachedInputPerMTok: 0.02, cacheWritePerMTok: 0.25, outputPerMTok: 1.2 },
  },
  'gpt-5.6-terra': {
    id: 'gpt-5.6-terra',
    label: 'GPT-5.6 Terra',
    family: 'gpt-5.6',
    promptCaching: 'explicit',
    description: 'Balanced judgement.',
    reasoningEfforts: GPT_5_6_EFFORTS,
    pricing: { inputPerMTok: 2, cachedInputPerMTok: 0.2, cacheWritePerMTok: 2.5, outputPerMTok: 12 },
  },
  'gpt-5.6-sol': {
    id: 'gpt-5.6-sol',
    label: 'GPT-5.6 Sol',
    family: 'gpt-5.6',
    promptCaching: 'explicit',
    description: 'Deepest reasoning of the 5.6 family.',
    reasoningEfforts: GPT_5_6_EFFORTS,
    pricing: { inputPerMTok: 4, cachedInputPerMTok: 0.4, cacheWritePerMTok: 5, outputPerMTok: 20 },
  },
  'gpt-6-luna': {
    id: 'gpt-6-luna',
    label: 'GPT-6 Luna',
    family: 'gpt-6',
    promptCaching: 'explicit',
    description: 'Most efficient current-generation model.',
    reasoningEfforts: GPT_5_6_EFFORTS,
    pricing: { inputPerMTok: 0.1, cachedInputPerMTok: 0.01, cacheWritePerMTok: 0.125, outputPerMTok: 0.5 },
  },
  'gpt-6-sol': {
    id: 'gpt-6-sol',
    label: 'GPT-6 Sol',
    family: 'gpt-6',
    promptCaching: 'explicit',
    description: 'Current-generation flagship reasoning.',
    reasoningEfforts: GPT_5_6_EFFORTS,
    pricing: { inputPerMTok: 2, cachedInputPerMTok: 0.2, cacheWritePerMTok: 2.5, outputPerMTok: 10 },
  },
  'gpt-6.1-sol': {
    id: 'gpt-6.1-sol',
    label: 'GPT-6.1 Sol',
    family: 'gpt-6',
    promptCaching: 'explicit',
    description: 'Latest flagship reasoning.',
    reasoningEfforts: GPT_5_6_EFFORTS,
    pricing: { inputPerMTok: 2, cachedInputPerMTok: 0.1, cacheWritePerMTok: 2.5, outputPerMTok: 10 },
  },
} as const satisfies Record<string, AiModel>;

export type AiModelId = keyof typeof AI_MODELS;

export const AI_MODEL_IDS = Object.keys(AI_MODELS) as AiModelId[];
export const AI_REASONING_EFFORTS: readonly AiReasoningEffort[] = ['none', 'low', 'medium', 'high', 'xhigh', 'max'];
export const AI_VERBOSITIES: readonly AiVerbosity[] = ['low', 'medium', 'high'];

export const AI_MAX_OUTPUT_TOKENS_MIN = 256;
export const AI_MAX_OUTPUT_TOKENS_MAX = 32_000;

export function isAiModelId(value: unknown): value is AiModelId {
  return typeof value === 'string' && Object.hasOwn(AI_MODELS, value);
}

export function getAiModel(id: string): AiModel | null {
  return isAiModelId(id) ? AI_MODELS[id] : null;
}

export function isAiReasoningEffort(value: unknown): value is AiReasoningEffort {
  return typeof value === 'string' && (AI_REASONING_EFFORTS as readonly string[]).includes(value);
}

export function isAiVerbosity(value: unknown): value is AiVerbosity {
  return typeof value === 'string' && (AI_VERBOSITIES as readonly string[]).includes(value);
}

export function isEffortSupported(modelId: string, effort: unknown): effort is AiReasoningEffort {
  const model = getAiModel(modelId);
  return !!model && typeof effort === 'string' && (model.reasoningEfforts as readonly string[]).includes(effort);
}
